import React, { useEffect, useRef, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const IpCidrInputField = ({
  ipAddress,
  subnet,
  onChange,
  disabled = false,
  onBlur,
  error = false,
}) => {
  const theme = useTheme();
  const [ip, setIp] = useState(["", "", "", ""]);
  const [subnetValue, setSubnetValue] = useState("");
  const inputRefs = useRef([]);
  const boxRef = useRef(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (ipAddress) {
      const formatValue = ipAddress.split(".");
      if (formatValue.length === 4 && formatValue.every((item) => /^\d{1,3}$/.test(item))) {
        setIp(formatValue);
      }
    } else {
      setIp(["", "", "", ""]);
    }
  }, [ipAddress]);

  useEffect(() => {
    setSubnetValue(subnet || "");
  }, [subnet]);

  const emitChange = (nextIp, nextSubnet) => {
    const allEmpty = nextIp.every((item) => item === "");
    const trimmedIp = [...nextIp];

    while (trimmedIp.length > 0 && trimmedIp[trimmedIp.length - 1] === "") {
      trimmedIp.pop();
    }

    onChange({
      ipAddress: allEmpty ? "" : trimmedIp.join("."),
      subnet: nextSubnet,
    });
  };

  const handleIpChange = (value, index) => {
    if (/^\d{0,3}$/.test(value)) {
      const nextIp = [...ip];
      nextIp[index] = value;
      setIp(nextIp);
      emitChange(nextIp, subnetValue);

      if (value.length === 3 && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;

    const pastedValue = e.clipboardData.getData("text").trim();
    const [pastedIp, pastedSubnet] = pastedValue.split("/");
    const formatValue = pastedIp.split(".");

    if (formatValue.length !== 4 || !formatValue.every((item) => /^\d{1,3}$/.test(item))) {
      return;
    }

    const nextSubnet =
      pastedSubnet !== undefined && /^\d{1,2}$/.test(pastedSubnet)
        ? pastedSubnet
        : subnetValue;

    e.preventDefault();
    setIp(formatValue);
    setSubnetValue(nextSubnet);
    emitChange(formatValue, nextSubnet);

    if (pastedSubnet !== undefined) {
      inputRefs.current[3]?.blur();
    } else {
      inputRefs.current[3]?.focus();
    }
  };

  const handleSubnetChange = (value) => {
    const nextSubnet = value.replace(/\D/g, "").slice(0, 2);
    setSubnetValue(nextSubnet);
    emitChange(ip, nextSubnet);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && ip[index] === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: `1px solid ${
          error
            ? theme.palette.primary.redPrimary
            : focused
              ? theme.palette.primary.greyPrimary
              : theme.palette.primary.greyFifth
        }`,
        borderRadius: "8px",
        padding: "4px 8px",
        gap: "4px",
        backgroundColor: theme.palette.primary.white,
        "&:hover": {
          borderColor: error ? theme.palette.primary.redPrimary : theme.palette.primary.mainHover,
        },
      }}
      ref={boxRef}
      tabIndex={0}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.relatedTarget || !boxRef.current.contains(e.relatedTarget)) {
          setFocused(false);
          onBlur?.();
        }
      }}
    >
      {ip.map((value, index) => (
        <React.Fragment key={index}>
          <TextField
            sx={{
              width: "42px",
              "& .MuiInputBase-root": {
                textAlign: "center",
              },
              "& .MuiInputBase-input": {
                textAlign: "center",
                padding: 0,
                color: theme.palette.primary.greyPrimary,
              },
              "& .MuiInput-underline:before, & .MuiInput-underline:after": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottom: "none",
              },
              backgroundColor: "transparent",
            }}
            inputProps={{ maxLength: 3, style: { textAlign: "center" } }}
            value={value}
            disabled={disabled}
            inputRef={(ref) => (inputRefs.current[index] = ref)}
            onChange={(e) => handleIpChange(e.target.value, index)}
            onPaste={handlePaste}
            onKeyDown={(e) => handleKeyDown(e, index)}
            variant="standard"
            autoComplete="off"
          />
          {index < 3 && (
            <Typography sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.text.primary }}>
              .
            </Typography>
          )}
        </React.Fragment>
      ))}
      <Typography sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.text.primary }}>
        /
      </Typography>
      <TextField
        sx={{
          width: "42px",
          "& .MuiInputBase-input": {
            textAlign: "center",
            padding: 0,
            color: theme.palette.primary.greyPrimary,
          },
          "& .MuiInput-underline:before, & .MuiInput-underline:after": {
            borderBottom: "none",
          },
          "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
            borderBottom: "none",
          },
          backgroundColor: "transparent",
        }}
        inputProps={{ maxLength: 2, style: { textAlign: "center" } }}
        value={subnetValue}
        disabled={disabled}
        onChange={(e) => handleSubnetChange(e.target.value)}
        variant="standard"
        autoComplete="off"
      />
    </Box>
  );
};

export default IpCidrInputField;
